import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { BtrStaticPageModel } from '@app/core/models/btr-static-page.model';
import { EventsService, SnackbarService } from '@app/core/services';
import { BtrStaticPageApiService } from '@app/shared/services/api';

const TSTATE_KEY = makeStateKey<BtrStaticPageModel>('static-page-resolver');

@Injectable()
export class StaticPageResolver implements Resolve<BtrStaticPageModel> {
  private readonly isServer: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly btrStaticPageApiService: BtrStaticPageApiService,
    private readonly transferState: TransferState,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private eventsService: EventsService,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  resolve(
    route: ActivatedRouteSnapshot,
  ):
    | Observable<BtrStaticPageModel>
    | Promise<BtrStaticPageModel>
    | BtrStaticPageModel
    | null {
    const staticPageSlug = route.paramMap.get('staticPageSlug');

    return this.isServer
      ? this.serverResolve(staticPageSlug)
      : this.browserResolve(staticPageSlug);
  }

  private browserResolve(
    slug: string,
  ): Observable<BtrStaticPageModel> | BtrStaticPageModel {
    const tmp = this.transferState.get<
      Pick<BtrStaticPageModel, keyof BtrStaticPageModel>
    >(TSTATE_KEY, null);
    this.eventsService.setIsLoading(true);
    if (tmp != null) {
      this.transferState.remove<BtrStaticPageModel>(TSTATE_KEY);
      return BtrStaticPageModel.copyObj(tmp);
    }

    return this.btrStaticPageApiService.getStaticPage({ slug }).pipe(
      catchError(err => {
        this.snackbarService.showError('Page not found', 1000);
        this.router.navigate(['page-not-found']);
        console.log(err);
        return of(null);
      }),
    );
  }

  private serverResolve(slug: string): Observable<BtrStaticPageModel> {
    return this.btrStaticPageApiService.getStaticPage({ slug }).pipe(
      tap(data => {
        this.transferState.set<BtrStaticPageModel>(TSTATE_KEY, data);
      }),
      catchError(err => {
        this.router.navigate(['page-not-found']);
        console.log(err);
        return of(null);
      }),
    );
  }
}
